import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Sparkles, Newspaper, Loader2 } from "lucide-react";

// Shared "Know Your Fees" viewer used by both the Trust site and institute public sites.
// Same 3 dropdowns (Admission Process, Course Type, Category) as the Trust site.
// Consumer passes the pre-selected institute — no institute switcher/name shown.

function currentAcademicYear() {
  const d = new Date();
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
}

function feesFormatINR(n) {
  const v = Number.isFinite(n) ? Number(n) : 0;
  return v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function InstituteFeesView({ instituteId, testidPrefix = "inst-fees" }) {
  const [ay] = useState(currentAcademicYear());
  const [masters, setMasters] = useState({ fee_admission_process: [], fee_course_type: [], fee_category: [] });
  const [available, setAvailable] = useState({ course_types: [], admission_processes: [], categories: [] });
  const [admProc, setAdmProc] = useState("");
  const [courseType, setCourseType] = useState("");
  const [category, setCategory] = useState("");
  const [table, setTable] = useState({ found: false, items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  const isInstituteLevel = /institute\s*level/i.test(admProc || "");

  useEffect(() => {
    if (!instituteId) return;
    let alive = true;
    (async () => {
      try {
        const [m, av] = await Promise.all([
          api.get("/public/fees/masters"),
          api.get("/public/fees/available", { params: { institute_id: instituteId, academic_year: ay } }),
        ]);
        if (!alive) return;
        setMasters(m.data || {});
        setAvailable(av.data || { course_types: [], admission_processes: [], categories: [] });
        const capMatch = (av.data?.admission_processes || []).find((x) => /cap/i.test(x))
          || (m.data?.fee_admission_process || []).find((x) => /cap/i.test(x?.name))?.name
          || "";
        setAdmProc(capMatch);
        setCourseType("");
        setCategory("");
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [instituteId, ay]);

  useEffect(() => {
    if (!instituteId || !ay || !courseType || !admProc) {
      setTable({ found: false, items: [], total: 0 });
      setStatus("");
      return;
    }
    const effCategory = isInstituteLevel ? "" : category;
    if (!isInstituteLevel && !effCategory) {
      setTable({ found: false, items: [], total: 0 });
      setStatus("");
      return;
    }
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/public/fees", {
          params: {
            institute_id: instituteId, academic_year: ay,
            course_type: courseType, admission_process: admProc,
            category: effCategory,
          },
        });
        if (!alive) return;
        setTable({ found: !!data?.found, items: data?.items || [], total: Number(data?.total) || 0 });
        setStatus(data?.found ? "" : "not_published");
      } catch {
        if (alive) { setTable({ found: false, items: [], total: 0 }); setStatus("error"); }
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [instituteId, ay, courseType, admProc, category, isInstituteLevel]);

  const admProcOptions = useMemo(() => (available.admission_processes?.length > 0
    ? available.admission_processes
    : (masters.fee_admission_process || []).map((x) => x.name)), [available.admission_processes, masters.fee_admission_process]);
  const courseTypeOptions = useMemo(() => (available.course_types?.length > 0
    ? available.course_types
    : (masters.fee_course_type || []).map((x) => x.name)), [available.course_types, masters.fee_course_type]);
  const categoryOptions = useMemo(() => (available.categories?.length > 0
    ? available.categories
    : (masters.fee_category || []).map((x) => x.name)), [available.categories, masters.fee_category]);

  return (
    <section className="max-w-6xl mx-auto px-5 py-14" data-testid={`${testidPrefix}-root`}>
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white rounded-2xl p-8 relative overflow-hidden shadow-lg" data-testid={`${testidPrefix}-header`}>
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-amber-500/15 blur-2xl" />
        <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-rose-500/15 blur-2xl" />
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-400">Know Your Fees · Academic Year</div>
          <h2 className="font-serif text-3xl sm:text-4xl mt-2 leading-tight">
            Institute Fee Structure <span className="text-amber-400">({ay})</span>
          </h2>
          <p className="mt-3 text-white/85 text-sm max-w-3xl">
            Select an <span className="font-semibold">Admission Process</span>, <span className="font-semibold">Course Type</span> and <span className="font-semibold">Category</span> below to view the published fee schedule. If Admission Process is <em>Institute Level</em>, Category is not required.
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-600 font-bold mb-1.5">Admission Process</div>
          <select data-testid={`${testidPrefix}-adm-process`} value={admProc} onChange={(e) => setAdmProc(e.target.value)} className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-700">
            <option value="">Select…</option>
            {admProcOptions.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-600 font-bold mb-1.5">Course Type</div>
          <select data-testid={`${testidPrefix}-course-type`} value={courseType} onChange={(e) => setCourseType(e.target.value)} className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-700">
            <option value="">Select…</option>
            {courseTypeOptions.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
        <label className={`block ${isInstituteLevel ? "opacity-60" : ""}`}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-600 font-bold mb-1.5 flex items-center justify-between">
            <span>Category</span>
            {isInstituteLevel && <span className="text-[9px] normal-case tracking-normal text-amber-600 font-semibold">N/A</span>}
          </div>
          <select data-testid={`${testidPrefix}-category`} value={isInstituteLevel ? "" : category} onChange={(e) => setCategory(e.target.value)} disabled={isInstituteLevel} className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-700 disabled:bg-slate-50">
            <option value="">{isInstituteLevel ? "— not applicable —" : "Select…"}</option>
            {categoryOptions.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
      </div>

      <div className="mt-8">
        {!(admProc && courseType) || (!isInstituteLevel && !category) ? (
          <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center" data-testid={`${testidPrefix}-empty`}>
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-amber-500/15 to-rose-500/15 text-slate-800">
              <Sparkles size={22} />
            </div>
            <div className="font-serif text-lg text-slate-900 mt-4">Pick the required filters to view fees</div>
            <p className="text-sm text-slate-500 mt-1">Choose Admission Process and Course Type. If Admission Process isn&apos;t <em>Institute Level</em>, also pick a Category.</p>
          </div>
        ) : loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading fee structure…</div>
        ) : !table.found || table.items.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center" data-testid={`${testidPrefix}-not-published`}>
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-rose-500/10 text-rose-600">
              <Newspaper size={22} />
            </div>
            <div className="font-serif text-lg text-slate-900 mt-4">Fee structure not published for this combination yet.</div>
            <p className="text-sm text-slate-500 mt-2">Please try a different combination or contact the institute office.</p>
            {status === "not_published" && <div className="mt-1 text-[11px] text-slate-400">Combination: {admProc} · {courseType}{!isInstituteLevel && ` · ${category}`}</div>}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`${testidPrefix}-table-card`}>
            <div className="px-5 py-3.5 bg-slate-50 border-b border-slate-200">
              <div className="text-[11px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Fee Schedule</div>
              <div className="text-sm text-slate-900 font-semibold mt-0.5">
                {ay} · <span className="text-slate-700">{courseType}</span> · <span className="text-amber-600">{admProc}</span>
                {!isInstituteLevel && (<> · <span className="text-rose-600">{category}</span></>)}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid={`${testidPrefix}-table`}>
                <thead className="bg-white border-b border-slate-200 text-[10px] tracking-[0.2em] uppercase text-slate-500">
                  <tr>
                    <th className="w-14 text-left px-5 py-3 font-semibold">#</th>
                    <th className="text-left px-5 py-3 font-semibold">Fee Head</th>
                    <th className="w-56 text-right px-5 py-3 font-semibold">Amount (₹)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {table.items.map((it, i) => (
                    <tr key={`${it.fee_header}-${i}`} data-testid={`${testidPrefix}-row-${i}`} className="hover:bg-slate-50">
                      <td className="px-5 py-2.5 text-slate-400 text-xs font-mono">{String(i + 1).padStart(2, "0")}</td>
                      <td className="px-5 py-2.5 text-slate-900">{it.fee_header}</td>
                      <td className="px-5 py-2.5 text-right font-mono tabular-nums text-slate-900">₹ {feesFormatINR(it.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gradient-to-r from-slate-800 to-slate-900 text-white" data-testid={`${testidPrefix}-total-row`}>
                    <td className="px-5 py-3" />
                    <td className="px-5 py-3 text-[11px] tracking-[0.2em] uppercase font-semibold">Grand Total</td>
                    <td className="px-5 py-3 text-right font-mono text-lg font-bold tabular-nums" data-testid={`${testidPrefix}-grand-total`}>
                      ₹ {feesFormatINR(table.total)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 text-[11px] text-slate-500">
              * Amounts are indicative and may be revised as per statutory / regulatory notifications. Please confirm with the institute office before making payment.
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
